import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Pill, Clock, Check, Bell, Plus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mock essentials data
const mockEssentials = [
  { id: '1', name: 'Vitamin D3', dosage: '2000 IU', time: '08:00', taken: false, type: 'supplement' },
  { id: '2', name: 'Omega-3', dosage: '1000mg', time: '08:00', taken: true, type: 'supplement' },
  { id: '3', name: 'Magnesium', dosage: '400mg', time: '21:00', taken: false, type: 'supplement' },
];

export default function EssentialsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [essentials, setEssentials] = useState(mockEssentials);
  const [showAdd, setShowAdd] = useState(false);
  const [newEssential, setNewEssential] = useState({ name: '', dosage: '', time: '08:00', type: 'supplement' });
  const [reminders, setReminders] = useState(true);

  const dueNow = essentials.filter(e => !e.taken && isTimeDue(e.time));
  const laterToday = essentials.filter(e => !e.taken && !isTimeDue(e.time));
  const completed = essentials.filter(e => e.taken);

  function isTimeDue(time) {
    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    const essentialTime = new Date();
    essentialTime.setHours(hours, minutes, 0, 0);
    return now >= essentialTime;
  }

  const handleMarkTaken = (id) => {
    setEssentials(prev => prev.map(e => 
      e.id === id ? { ...e, taken: true } : e
    ));
    toast.success('Marked as taken!');
  };

  const handleSkip = (id) => {
    setEssentials(prev => prev.filter(e => e.id !== id));
    toast.info('Skipped for today');
  };

  const handleAddEssential = () => {
    if (!newEssential.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    const essential = {
      id: Date.now().toString(),
      ...newEssential,
      taken: false
    };
    
    setEssentials(prev => [...prev, essential]);
    setNewEssential({ name: '', dosage: '', time: '08:00', type: 'supplement' });
    setShowAdd(false);
    toast.success('Essential added!');
  };

  const streak = essentials.filter(e => e.taken).length;
  const total = essentials.length;

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-lg">Essentials</h1>
              <p className="text-xs text-muted-foreground">Meds & Supplements</p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Progress Card */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card p-5" data-testid="essentials-progress">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-muted-foreground">Today's Progress</p>
                <p className="text-2xl font-bold font-heading">
                  {streak}/{total} <span className="text-sm font-normal text-muted-foreground">taken</span>
                </p>
              </div>
              <div className="p-3 rounded-2xl bg-primary/10">
                <Pill size={24} className="text-primary" />
              </div>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(streak / total) * 100}%` }}
                className="h-full bg-[#8BB89F] rounded-full"
              />
            </div>
          </Card>
        </motion.section>

        {/* Due Now */}
        {dueNow.length > 0 && (
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <Clock size={14} />
              Due Now
            </h2>
            <div className="space-y-3">
              {dueNow.map((essential, index) => (
                <motion.div
                  key={essential.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="glass-card p-4 border-l-4 border-l-primary">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-primary/10">
                          <Pill size={18} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="font-medium">{essential.name}</h3>
                          <p className="text-xs text-muted-foreground">
                            {essential.dosage} • {essential.time}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSkip(essential.id)}
                          className="text-muted-foreground"
                        >
                          Skip
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleMarkTaken(essential.id)}
                          className="bg-[#8BB89F] hover:bg-[#8BB89F]/90 rounded-full"
                          data-testid={`take-${essential.id}`}
                        >
                          <Check size={14} className="mr-1" />
                          Take
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Later Today */}
        {laterToday.length > 0 && (
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Later Today
            </h2>
            <div className="space-y-3">
              {laterToday.map((essential, index) => (
                <Card key={essential.id} className="glass-card p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-muted">
                        <Pill size={18} className="text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-medium">{essential.name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {essential.dosage} • {essential.time}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{essential.time}</span>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Completed */}
        {completed.length > 0 && (
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Completed
            </h2>
            <div className="space-y-3">
              {completed.map((essential) => (
                <Card key={essential.id} className="glass-card p-4 opacity-60">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-[#8BB89F]/20">
                        <Check size={18} className="text-[#8BB89F]" />
                      </div>
                      <div>
                        <h3 className="font-medium line-through">{essential.name}</h3>
                        <p className="text-xs text-muted-foreground">{essential.dosage}</p>
                      </div>
                    </div>
                    <span className="text-xs text-[#8BB89F]">Done</span>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Reminders Toggle */}
        <Card className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell size={18} className="text-muted-foreground" />
              <span>Reminders</span>
            </div>
            <Switch 
              checked={reminders} 
              onCheckedChange={setReminders}
              data-testid="reminders-toggle"
            />
          </div>
        </Card>

        {/* Add Button */}
        <Button
          onClick={() => setShowAdd(true)}
          variant="outline"
          className="w-full rounded-full"
          data-testid="add-essential-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Essential
        </Button>
      </main>

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm"
          >
            <Card className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-xl">Add Essential</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAdd(false)}
                  className="rounded-full"
                >
                  <X size={18} />
                </Button>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    placeholder="e.g., Vitamin C"
                    value={newEssential.name}
                    onChange={(e) => setNewEssential(prev => ({ ...prev, name: e.target.value }))}
                    className="rounded-xl h-12"
                    data-testid="essential-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dosage</Label>
                  <Input
                    placeholder="e.g., 500mg"
                    value={newEssential.dosage}
                    onChange={(e) => setNewEssential(prev => ({ ...prev, dosage: e.target.value }))}
                    className="rounded-xl h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={newEssential.time}
                    onChange={(e) => setNewEssential(prev => ({ ...prev, time: e.target.value }))}
                    className="rounded-xl h-12"
                  />
                </div>
              </div>
              <Button
                onClick={handleAddEssential}
                className="w-full bg-primary hover:bg-primary/90 rounded-full mt-6"
                data-testid="save-essential-btn"
              >
                Add Essential
              </Button>
            </Card>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
